let tokenRequest = null;

// generateAuthToken() resolves quickly for a signed-IN user, but for a signed-OUT
// user the Catalyst SDK can hang / make a doomed remote/auth request (no session →
// 500 + CORS console noise, and a slow fallthrough). We (1) only call it when there's
// a hint the user is authenticated, and (2) time-bound it as a safety net so it can
// never freeze the app's session check on the "One moment." loader.
const TOKEN_TIMEOUT_MS = 4000;

// Cheap, HttpOnly-cookie-free signal that a token is worth minting:
//   - persisted auth state (a returning signed-in user), or
//   - a sign-in attempt in the last 2 min (first login, just back from the hosted
//     redirect — the token is needed but the store isn't populated yet).
// Signed-out visitors have neither → we skip generateAuthToken entirely, so their
// load is instant and the console is clean.
function hasSessionHint() {
  try {
    const persisted = JSON.parse(localStorage.getItem('speech-coach-auth') || 'null');
    if (persisted && persisted.state && persisted.state.isAuthenticated) return true;
    const attempts = JSON.parse(localStorage.getItem('larynx_signin_attempts') || '[]');
    if (Array.isArray(attempts)) {
      const now = Date.now();
      if (attempts.some((t) => typeof t === 'number' && now - t < 2 * 60 * 1000)) return true;
    }
  } catch (_) { /* localStorage unavailable — treat as no hint */ }
  return false;
}

export async function getCatalystAuthToken() {
  if (!hasSessionHint()) return null; // signed-out: skip the doomed/slow token call

  const generateAuthToken = window.catalyst?.auth?.generateAuthToken;
  if (typeof generateAuthToken !== 'function') return null;

  if (!tokenRequest) {
    tokenRequest = Promise.resolve(generateAuthToken.call(window.catalyst.auth))
      // Swallow rejection so a failed token mint (common when signed out) doesn't
      // surface as an "Uncaught (in promise)" on the shared promise.
      .catch(() => null)
      .finally(() => { tokenRequest = null; });
  }

  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS);
  });

  const response = await Promise.race([tokenRequest, timeout]);
  clearTimeout(timer);
  return typeof response?.access_token === 'string' && response.access_token
    ? response.access_token
    : null;
}

export async function catalystFetch(input, options = {}) {
  const target = new URL(input, window.location.origin);
  const isSameOrigin = target.origin === window.location.origin;
  let token = null;

  if (isSameOrigin) {
    try {
      token = await getCatalystAuthToken();
    } catch (_) {
      // Anonymous calls continue without a token and receive the normal 401 response.
    }
  }

  return fetch(input, {
    credentials: 'include',
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { Authorization: token } : {}),
    },
  });
}
